/**
 * useCompanionCaseMessage
 *
 * All business logic for CompanionCaseMessageModal:
 * - Case + contact data via React Query
 * - Contact checkbox selections
 * - File upload
 * - Message submission
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { sortByKey } from 'utils/utils';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { getClientUserList } from 'services/CaseServices';

import useCompanionCase from 'modules/cases/hooks/useCompanionCase';
import { useCreateMessage } from './useMessageMutations';
import { useFileUpload } from './useFileUpload';

// ── Pure helpers ──────────────────────────────────────────────────────────────

const toContact = (client) => ({
  contact_id: client.contact_id || client.user_id,
  client_cognito_username: client.client_cognito_username,
  full_name: generateFullName(client),
  is_primary_contact: client.is_primary_contact,
  first_name: client.first_name,
});

const toCaseItem = (caseId, caseName, rawClients) => ({
  case_id: caseId,
  case_name: caseName,
  contacts: sortByKey((rawClients || []).filter((c) => c.is_create_client_portal).map(toContact), 'first_name'),
});

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * @param {{ caseDetails: object|null, show: boolean }} params
 */
export function useCompanionCaseMessage({ caseDetails, show }) {
  const { addToast } = useToast();
  const createMessageMutation = useCreateMessage();
  const { files, addFiles, removeFile, clearFiles, uploadFiles, uploadProgress, isUploading } = useFileUpload();

  // ── Data fetching (React Query) ───────────────────────────────────────────
  const { data: mainCaseData } = useQuery({
    queryKey: ['companion-case-message-contacts', caseDetails?.case_id],
    queryFn: () => getClientUserList(caseDetails.case_id, false, false, true),
    enabled: show && !!caseDetails?.case_id,
    select: (res) => res?.data || [],
  });

  const { companionList } = useCompanionCase(caseDetails?.case_id);

  // ── Derived case + contact lists ──────────────────────────────────────────
  const mainCase = useMemo(() => {
    if (!mainCaseData?.length || !caseDetails) return null;
    return toCaseItem(caseDetails.case_id, caseDetails.case_description?.case_name || 'Main Case', mainCaseData);
  }, [mainCaseData, caseDetails]);

  const companionCases = useMemo(() => {
    return (companionList || []).filter((c) => !c.is_lead).map((c) => toCaseItem(c.case_id, c.case_name, c.lawft_clients));
  }, [companionList]);

  const allCases = useMemo(() => [mainCase, ...companionCases].filter(Boolean), [mainCase, companionCases]);

  // ── Checkbox selections ───────────────────────────────────────────────────

  const [selections, setSelections] = useState({});

  // Reset selections when dialog reopens
  useEffect(() => {
    if (!show) setSelections({});
  }, [show]);

  const handleSelect = useCallback((caseId, contactId, checked) => {
    setSelections((prev) => ({
      ...prev,
      [caseId]: { ...(prev[caseId] || {}), [contactId]: checked },
    }));
  }, []);

  const handleQuickSelectPrimary = useCallback(() => {
    setSelections((prev) => {
      const next = { ...prev };
      allCases.forEach(({ case_id, contacts }) => {
        next[case_id] = { ...(next[case_id] || {}) };
        contacts
          .filter((c) => c.is_primary_contact)
          .forEach((c) => {
            next[case_id][c.contact_id] = true;
          });
      });
      return next;
    });
  }, [allCases]);

  // ── Derived counts ────────────────────────────────────────────────────────

  const totalSelected = useMemo(
    () => Object.values(selections).reduce((sum, caseSelections) => sum + Object.values(caseSelections).filter(Boolean).length, 0),
    [selections]
  );

  const hasMainCaseSelected = useMemo(
    () => (mainCase ? Object.values(selections[mainCase.case_id] || {}).some(Boolean) : false),
    [mainCase, selections]
  );

  // ── File drop ─────────────────────────────────────────────────────────────

  const handleFileDrop = useCallback(
    (dropped) => {
      // if (files.length + dropped.length > 20) {
      //   addToast(false, toastConstant.toasterType.WARN, toastConstant.header.WARNINIG, toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED);
      //   return;
      // }
      addFiles(dropped);
    },
    [files.length, addFiles, addToast]
  );

  // ── Submit ────────────────────────────────────────────────────────────────

  const sendMessages = useCallback(
    async (formData, { onSuccess } = {}) => {
      const uploadedAttachments = await uploadFiles(caseDetails?.case_id);
      if (uploadedAttachments === null) return;

      const companion_case_config = allCases.reduce((config, caseItem) => {
        const recipients = Object.entries(selections[caseItem.case_id] || {})
          .filter(([, checked]) => checked)
          .map(([contactId]) => {
            const contact = caseItem.contacts.find((c) => c.contact_id === contactId);
            return {
              contact_id: contact.contact_id,
              client_cognito_username: contact.client_cognito_username,
            };
          });
        if (recipients.length) config[caseItem.case_id] = recipients;
        return config;
      }, {});

      await createMessageMutation.mutateAsync({
        groups: [],
        subject: formData.subject,
        message: formData.message,
        attachment: uploadedAttachments || [],
        event_attachment: [],
        task_attachment: [],
        companion_case_config,
        case_id: caseDetails?.case_id,
        message_to: [],
      });

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Messages sent successfully');
      onSuccess?.();
    },
    [uploadFiles, caseDetails?.case_id, allCases, selections]
  );

  // ── Reset ─────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    document.activeElement?.blur();
    clearFiles();
    setSelections({});
  }, [clearFiles]);

  return {
    // Case data
    mainCase,
    allCases,
    // Selections
    selections,
    totalSelected,
    hasMainCaseSelected,
    handleSelect,
    handleQuickSelectPrimary,
    // Files
    files,
    uploadProgress,
    isUploading,
    removeFile,
    handleFileDrop,
    // Submit
    sendMessages,
    isSending: createMessageMutation.isLoading,
    // Reset
    reset,
  };
}
